import { inject, Injectable } from '@angular/core';
import {
  type MarkAlertedLearningResourceItemReadInput,
  type MarkAlertedLearningResourceItemReadMutation,
  type MarkAlertedLearningResourceItemReadMutationVariables,
  type ViewerWithAlertedLearningResourceSetItemsQuery,
  type ViewerWithAlertedLearningResourceSetItemsQueryVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { learningResourceArticleMinimal } from '../learning-resource-article.minimal';
import { learningResourceFileMinimal } from '../learning-resource-file.minimal';
import { learningResourceLinkMinimal } from '../learning-resource-link.minimal';
import { learningResourceMicroLearningMinimal } from '../learning-resource-micro-learning.minimal';

@Injectable({ providedIn: 'root' })
export class SharedAlertedLearningResourceItemApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchUnreadAlertedLearningResourceItemsForViewer() {
    return (
      this.sharedGraphQlService
        .watchQuery<
          ViewerWithAlertedLearningResourceSetItemsQuery,
          ViewerWithAlertedLearningResourceSetItemsQueryVariables
        >({
          query: gql`
            query ViewerWithAlertedLearningResourceSetItems {
              viewer {
                id
                unreadAlertedLearningResourceItems {
                  nodes {
                    id
                    readAt
                    learningResourceSet {
                      id
                      learningResourceGlobalCategory {
                        id
                        application
                      }
                    }
                    learningResourceItem {
                      ... on LearningResourceArticle {
                        ...LearningResourceArticleMinimal
                      }

                      ... on LearningResourceMicroLearning {
                        ...LearningResourceMicroLearningMinimal
                      }

                      ... on LearningResourceLink {
                        ...LearningResourceLinkMinimal
                      }

                      ... on LearningResourceFile {
                        ...LearningResourceFileMinimal
                      }
                    }
                  }
                }
              }
            }
            ${learningResourceArticleMinimal}
            ${learningResourceMicroLearningMinimal}
            ${learningResourceLinkMinimal}
            ${learningResourceFileMinimal}
          `,
        })
        // alerts that have been read are not returned from the api, however after marking them as read,
        // they are updated in the cache. By using a filter here, we don't have to refetch this query.
        // After the next network request, these alerts won't be returned anymore.
        // @ts-expect-error TS18047 possibly null | TS18049 possibly null or undefined
        .pipe(map((res) => res.data.viewer.unreadAlertedLearningResourceItems.nodes.filter((alri) => !alri.readAt)))
    );
  }

  markAlertedLearningResourceItemRead(input: MarkAlertedLearningResourceItemReadInput) {
    // we don't have to refetch a new list of alerts as the watchQuery will filter out read alerts,
    // so we need to retrieve the readAt field to update the cache.
    return this.sharedGraphQlService.mutateV2<
      MarkAlertedLearningResourceItemReadMutation,
      MarkAlertedLearningResourceItemReadMutationVariables
    >({
      mutation: gql`
        mutation MarkAlertedLearningResourceItemRead($input: MarkAlertedLearningResourceItemReadInput!) {
          markAlertedLearningResourceItemRead(input: $input) {
            alertedLearningResourceSetItemForUser {
              id
              readAt
            }
            errors {
              message
              path
            }
          }
        }
      `,
      variables: { input },
    });
  }
}
